import { createApp } from "vue";
import Chartkick from "vue-chartkick";
import VueGtag from "vue-gtag";
import { plugin as VueTippy } from "vue-tippy";

import "@fontsource/inter/latin.css";
import * as Sentry from "@sentry/vue";
import { VueQueryPlugin } from "@tanstack/vue-query";
import "chartkick/chart.js";
import { vMaska } from "maska";
import mitt from "mitt";
import { createPinia } from "pinia";
import "tippy.js/dist/tippy.css";

import "@/assets/global.css";
import "@/assets/quill.css";
import "@/assets/tailwind.css";
import config from "@/config";
import vTooltipable from "@/directives/tooltipable";
import globalComponents from "@/globalComponents";
import { shouldIgnoreEvent } from "@/helpers/sentry";
import { establishWebSocketConnection } from "@/helpers/websockets";
import i18n from "@/i18n";
import ClickOutsidePlugin from "@/plugins/ClickOutsidePlugin";
import { queryClient } from "@/query/queryClient";
import router from "@/router";
import store from "@/store";
import { useAnalyticsStore } from "@/stores/analytics";

import App from "@/App.vue";

import type { ILfVuexStore } from "@/models/state";
import type { Directive, Plugin } from "vue";

const app = createApp(App)
  // Pinia **HAS** to be added before router and other plugins
  // because they might depend on it.
  .use(createPinia())
  .use(router)
  .use(i18n)
  .use(store as ILfVuexStore & Plugin)
  // eslint-disable-next-line
  // @ts-ignore
  .use(Chartkick)
  .use(ClickOutsidePlugin)
  .use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: true })
  .use(VueTippy, {
    component: "tooltip",
    directive: "tooltip",
    defaultProps: { theme: "lendflow" },
  });

Object.entries(globalComponents).forEach(([name, component]) =>
  app.component(name, component)
);
// eslint-disable-next-line no-console
console.log("coming from dashboard");

app.config.globalProperties.emitter = mitt();

if (config.env === "production") {
  if (config.google.googleTagId) {
    app.use(
      VueGtag,
      {
        config: {
          id: config.google.googleTagId,
        },
        appName: "dashboard",
        deferScriptLoad: true,
      },
      router
    );
  }
}

establishWebSocketConnection();

Sentry.init({
  app,
  dsn: config.sentryDashboardDsn,
  release: config.sentryRelease,
  dist: config.sentryDist,
  sampleRate: 1,
  tracesSampleRate: 0,
  maxBreadcrumbs: 50,
  beforeSend: (event) =>
    shouldIgnoreEvent(event.exception?.values) ? null : event,
});

app.directive("maska", vMaska as Directive);
app.directive("tooltipable", vTooltipable);

app.mount("#app");

// Initialize analytics after app mount
useAnalyticsStore();

// Clear impersonation flag after app loads to prevent permission errors during impersonation
if (localStorage.getItem("impersonationInProgress") === "true") {
  setTimeout(() => {
    localStorage.removeItem("impersonationInProgress");
  }, 5000);
}
